import { useCallback, useMemo } from 'react';

import { MatcherSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { AiSuggestionBeneficiaryDesignationTier } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useFetchBeneficiaryDesignationSuggestionsQuery } from '../graphql/BeneficiaryDesignationSuggestions.generated';

interface UseBeneficiaryDesignationSuggestionsArgs {
  entityId: string | null | undefined;
}

export interface BeneficiaryDesignationSuggestionsByTier {
  primary: MatcherSuggestion[];
  contingent: MatcherSuggestion[];
  loading: boolean;
  /** Re-runs the suggestions query, e.g. after a manual regeneration. */
  refetch: () => void;
}

/**
 * Fetches the account's un-acknowledged AI beneficiary-designation suggestions
 * (DP_RECIPIENT_V2 with a non-null tier) and splits them into the editor's
 * primary and contingent buckets by `dpBeneficiaryDesignationTier`.
 *
 * Suggestions the user has accepted or ignored during this edit session are
 * filtered out immediately so the relevant card row disappears on action; the
 * acknowledgement itself is persisted via `acknowledgeAISuggestions` when the
 * form is saved, after which the refetched query no longer returns them.
 */
export function useBeneficiaryDesignationSuggestions({
  entityId,
}: UseBeneficiaryDesignationSuggestionsArgs): BeneficiaryDesignationSuggestionsByTier {
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();

  const { data, loading, refetch } =
    useFetchBeneficiaryDesignationSuggestionsQuery({
      variables: { entityId: entityId ?? '' },
      skip: !entityId,
      fetchPolicy: 'cache-and-network',
      // `dpBeneficiaryDesignationTier` only resolves once the BE-5 suggestion
      // backend (T1-5924) is deployed. Until then treat any error as "no
      // suggestions" so the editor still renders rather than erroring out.
      errorPolicy: 'all',
    });

  const refetchSuggestions = useCallback(() => {
    void refetch();
  }, [refetch]);

  return useMemo(() => {
    const primary: MatcherSuggestion[] = [];
    const contingent: MatcherSuggestion[] = [];

    for (const node of getNodes(data?.beneficiaryDesignationSuggestions)) {
      if (acknowledgedSuggestions[node.id]) {
        continue;
      }
      if (
        node.dpBeneficiaryDesignationTier ===
        AiSuggestionBeneficiaryDesignationTier.Primary
      ) {
        primary.push(node);
      } else if (
        node.dpBeneficiaryDesignationTier ===
        AiSuggestionBeneficiaryDesignationTier.Contingent
      ) {
        contingent.push(node);
      }
    }

    return { primary, contingent, loading, refetch: refetchSuggestions };
  }, [data, acknowledgedSuggestions, loading, refetchSuggestions]);
}
